/**
 * Arithmétique décimale exacte et politique d'arrondi.
 *
 * Architecture §9. C'est la section qui décide du premier critère d'acceptation
 * du produit : « un déboursé réel importé produit exactement les mêmes totaux que
 * le classeur de référence ». Les nombres à virgule des ordinateurs sont binaires
 * (`0.1 + 0.2` ne fait pas exactement `0.3`) ; sur des milliers d'opérations
 * enchaînées, l'écart remonte jusqu'au centime affiché. Un total qui annonce
 * 2 341 892,99 € face à 2 341 893,00 € est techniquement négligeable et
 * fonctionnellement fatal.
 *
 * Tout ce fichier existe pour que ce cas n'arrive jamais.
 */
// Import nommé, et non par défaut : le fichier de types de `decimal.js` déclare
// à la fois une classe et un espace de noms du même nom, et l'import par défaut
// ne ramène que le second — d'où un type inutilisable.
import { Decimal } from "decimal.js";
/**
 * 34 chiffres significatifs : très au-delà de ce que la chaîne
 * `temps unitaire × quantité × prix unitaire` demande, et sans coût perceptible
 * aux volumes du produit (~700 lignes par chantier).
 *
 * `toExpNeg` / `toExpPos` aux extrêmes : sans eux, `toString()` bascule en
 * notation exponentielle au-delà de 1e21, et un montant partirait en base sous la
 * forme « 1e+21 ». PostgreSQL l'accepte, l'humain qui relit la table non.
 */
Decimal.set({
    precision: 34,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -9e15,
    toExpPos: 9e15,
});
export { Decimal };
/**
 * Nombre de décimales par nature de valeur. Ces valeurs sont celles du schéma :
 * `NUMERIC(14,2)` pour les montants, `NUMERIC(12,4)` pour les heures et les temps
 * unitaires. Les deux doivent rester d'accord — un arrondi à 2 décimales écrit
 * dans une colonne à 4 se relit avec deux zéros de trop, et l'inverse tronque en
 * silence côté base.
 */
export const DECIMALES = {
    montant: 2,
    heure: 4,
    quantite: 4,
};
/**
 * Construit une valeur décimale exacte.
 *
 * Les chaînes sont la forme normale : c'est ce que `node-postgres` rend pour une
 * colonne `NUMERIC`, et c'est ce qu'on lui redonne.
 *
 * **Un nombre JavaScript à virgule est refusé.** `dec(0.0292)` paraît anodin mais
 * la valeur est déjà fausse avant d'entrer ici — la bibliothèque décimale
 * reprendrait fidèlement une erreur commise en amont, et la garantie de cette
 * section serait perdue au premier appel. Les entiers, eux, sont exacts tant
 * qu'ils tiennent dans un entier sûr : ils sont acceptés, parce qu'écrire
 * `dec(3)` pour trois demi-journées est naturel et sans risque.
 */
export function dec(valeur) {
    if (valeur instanceof Decimal)
        return valeur;
    if (typeof valeur === "number") {
        if (!Number.isFinite(valeur)) {
            throw new Error(`dec() a reçu une valeur non finie : ${String(valeur)}`);
        }
        if (!Number.isInteger(valeur)) {
            throw new Error(`dec() refuse le nombre à virgule ${valeur} : sa valeur est déjà approchée ` +
                `avant d'arriver ici. Passer une chaîne — dec("${valeur}") — ou une valeur décimale.`);
        }
        if (!Number.isSafeInteger(valeur)) {
            throw new Error(`dec() refuse l'entier ${valeur} : il dépasse la plage des entiers exacts.`);
        }
        return new Decimal(valeur);
    }
    const texte = valeur.trim();
    if (texte === "") {
        throw new Error("dec() a reçu une chaîne vide.");
    }
    // Une virgule décimale est tolérée : les fichiers de déboursé et les saisies
    // d'écran en portent, et laisser chaque appelant faire le remplacement de son
    // côté produirait un jour un appelant qui l'oublie.
    const normalise = texte.replace(",", ".");
    let d;
    try {
        d = new Decimal(normalise);
    }
    catch {
        // La bibliothèque lève « [DecimalError] Invalid argument », qui ne dit ni
        // d'où vient la valeur ni ce qu'on en attendait. Ce message-ci se retrouvera
        // dans un rapport d'import, en face d'un numéro de ligne de tableur.
        throw new Error(`dec() a reçu une valeur illisible : « ${texte} »`);
    }
    // `Decimal` accepte « NaN » et « Infinity » comme entrées valides : ce contrôle
    // n'est donc pas redondant avec celui du dessus.
    if (!d.isFinite()) {
        throw new Error(`dec() a reçu une valeur illisible : « ${texte} »`);
    }
    return d;
}
/* ---------------------------------------------------------------------------
 * POLITIQUE D'ARRONDI — un seul endroit dans tout le produit
 * ---------------------------------------------------------------------------
 *
 * À quel niveau on arrondit (ligne, ouvrage, total) et dans quel ordre est une
 * RÈGLE MÉTIER, pas un détail d'implémentation : selon l'ordre choisi, le total
 * change au centime près.
 *
 * La règle retenue :
 *   1. toute la chaîne de calcul se fait en précision pleine ;
 *   2. on n'arrondit qu'à la RESTITUTION — écriture en base, ou affichage ;
 *   3. aucun écran, aucune requête SQL ne réarrondit de son côté.
 *
 * Le mode est « moitié vers le haut » (ROUND_HALF_UP), celui du tableur de
 * référence — et non le « moitié au pair » des bibliothèques bancaires.
 *
 * Cette politique est à confirmer sur les premiers imports réels (architecture
 * §9.3) : si l'ERP arrondit ligne à ligne, c'est ici que ça se change, une fois.
 */
export function arrondirMontant(valeur) {
    return dec(valeur).toDecimalPlaces(DECIMALES.montant, Decimal.ROUND_HALF_UP);
}
export function arrondirHeure(valeur) {
    return dec(valeur).toDecimalPlaces(DECIMALES.heure, Decimal.ROUND_HALF_UP);
}
export function arrondirQuantite(valeur) {
    return dec(valeur).toDecimalPlaces(DECIMALES.quantite, Decimal.ROUND_HALF_UP);
}
/**
 * Forme d'écriture en base : une chaîne à nombre de décimales fixe.
 *
 * On ne confie jamais un objet décimal au pilote de base : il l'enverrait via sa
 * conversion par défaut, qui repasse par un nombre à virgule.
 */
export function pourBase(valeur, decimales) {
    return valeur.toFixed(decimales);
}
/* ---------------------------------------------------------------------------
 * Mise en forme pour l'affichage — également à un seul endroit
 * ------------------------------------------------------------------------- */
/**
 * `2341893` → `« 2 341 893,00 »`.
 *
 * Écrit à la main plutôt que par `Intl.NumberFormat`, qui n'accepte qu'un nombre
 * JavaScript en entrée : la valeur exacte serait reperdue au moment même de
 * l'afficher, c'est-à-dire au seul endroit où quelqu'un la lit.
 *
 * Le séparateur de milliers est une **espace insécable étroite** (U+202F), la
 * convention typographique française : un montant ne doit jamais se couper en fin
 * de ligne.
 */
/**
 * **Le `number` est volontairement absent de ces trois signatures.**
 *
 * `dec()` accepte un entier — `dec(3)` pour trois demi-journées est naturel — mais refuse un
 * nombre à virgule, à l'exécution seulement. Un écran qui écrivait `formaterMontant(a - b)`
 * compilait donc parfaitement et levait en plein rendu, ce qui blanchit la page entière : c'est
 * la panne du 18/08/2026 sur l'écran des soumissions, doublée d'un courriel de validation qui
 * n'est jamais parti.
 *
 * Interdire le `number` ici met ce contrôle dans le compilateur. L'appelant doit dire ce qu'il
 * veut — `.toFixed(2)` pour un montant calculé à l'affichage — au lieu de le découvrir en
 * production. Ces fonctions ne servent qu'à afficher : aucune n'a besoin d'un nombre brut.
 */
export function formaterMontant(valeur) {
    return formater(arrondirMontant(valeur), DECIMALES.montant);
}
export function formaterHeure(valeur) {
    return formater(arrondirHeure(valeur), DECIMALES.heure);
}
/**
 * Même mise en forme, à un nombre de décimales choisi.
 *
 * Sert aux grandeurs dont l'échelle dépend de l'unité — une quantité de vente
 * s'écrit « 1 250 » en m² et « 12,5 » en tonnes. Elles passent par ici plutôt que
 * par un formatage improvisé dans un écran : le séparateur de milliers et la
 * virgule décimale ne se décident pas deux fois.
 */
export function formaterNombre(valeur, decimales) {
    return formater(dec(valeur).toDecimalPlaces(decimales, Decimal.ROUND_HALF_UP), decimales);
}
/**
 * Une distance en kilomètres, telle que les tables de distances la portent (072).
 *
 * **Une décimale, et le zéro final tombe.** Mesuré sur la production : 3 996 des 21 859
 * distances renseignées portent une décimale — dont des trajets de 0,8 km, qu'un arrondi à
 * l'entier afficherait « 1 km » — et **aucune n'en porte deux**. Arrondir à l'entier jetterait
 * donc une information que le référentiel détient sur près d'une ligne sur cinq ; en afficher
 * deux inventerait une précision qui n'existe pas.
 *
 * Le zéro final tombe pour la même raison : « 31,0 km » se lit comme une mesure au décimètre
 * près, alors que la table dit simplement 31.
 */
export function formaterKm(valeur) {
    const avecDecimale = formaterNombre(valeur, 1);
    return avecDecimale.endsWith(",0") ? avecDecimale.slice(0, -2) : avecDecimale;
}
function formater(valeur, decimales) {
    const fixe = valeur.toFixed(decimales);
    const negatif = fixe.startsWith("-");
    const sansSigne = negatif ? fixe.slice(1) : fixe;
    const [entiere = "0", fraction = ""] = sansSigne.split(".");
    // Espace insécable étroite, écrite par son point de code : un caractère
    // invisible dans du code source finit toujours par être remplacé par une
    // espace ordinaire au fil d'un copier-coller, et le montant se coupe alors en
    // fin de ligne sans que personne comprenne pourquoi.
    const separateurMilliers = "\u202f";
    let groupee = "";
    for (let i = 0; i < entiere.length; i++) {
        const restant = entiere.length - i;
        if (i > 0 && restant % 3 === 0)
            groupee += separateurMilliers;
        groupee += entiere.charAt(i);
    }
    const corps = fraction === "" ? groupee : `${groupee},${fraction}`;
    return negatif ? `-${corps}` : corps;
}
//# sourceMappingURL=nombre.js.map